import { useAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { changeProjectStatusConfirmDialogAtom } from '../atoms';
import { useOnClickOutside } from '../hooks';
import { useChangeProjectStatusMutation } from '../lib/queries';
import { QUERY_KEYS, TOAST_DURATION } from '../lib/constants';

export function ChangeProjectStatusConfirmDialog() {
  const { t } = useTranslation();
  const [state, setState] = useAtom(changeProjectStatusConfirmDialogAtom);
  const dialog = useRef<HTMLDialogElement | null>(null);
  const changeStatusMutation = useChangeProjectStatusMutation();
  const queryClient = useQueryClient();

  const handleClose = () => {
    setState({ isOpen: false, project: undefined, status: undefined });
    dialog.current?.close();
  };

  const handleSubmit = () => {
    if (state.project && state.status)
      changeStatusMutation.mutate(
        { project: state.project, status: state.status },
        {
          onSuccess: () => {
            toast.success(
              t('project.statusChanged', {
                name: state.project?.name,
                status: t(`project.status.${state.status}`),
              }),
              { duration: TOAST_DURATION },
            );
            handleClose();
            queryClient.refetchQueries({ queryKey: [QUERY_KEYS.PROJECTS] });
          },
        },
      );
  };

  useOnClickOutside(dialog.current, () => {
    if (state.isOpen) handleClose;
  });

  useEffect(() => {
    if (!dialog.current?.open && state.isOpen) dialog.current?.showModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isOpen]);

  return (
    <dialog ref={dialog} className="max-w-xl">
      <span className="whitespace-normal text-title">
        {t('project.changeStatusDialog.description', { name: state.project?.name })}
      </span>

      <div className="flex justify-end gap-2">
        <button className="primary-fill" onClick={handleSubmit}>
          {t('common.yes')}
        </button>
        <button className="primary-outline" onClick={handleClose}>
          {t('common.cancel')}
        </button>
      </div>
    </dialog>
  );
}
